// frontend/src/pages/SelfServiceHandbook.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Search, X, Menu, Printer,
  CheckCircle2, FileText, CalendarDays,
  UserCog, History, ListTree, ArrowUp, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { InfoBadge } from '../components/UI/InfoBadge';
import Portal from '../components/UI/Portal';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { Block } from '../components/handbook/DocumentBlocks';
import { useHandbook, useHandbookAcknowledgement, useAcknowledgeHandbook } from '../hooks/useHandbookData';
import { formatHandbookEditionLabel } from '../utils/handbookUtils';
import { filterDocumentSections } from '../utils/documentSectionUtils';
import weplLogo from '../assets/images/wepl_logo.png';
import handbookCover from '../assets/images/handbook_cover.png';
import {
  handbookMeta as staticMeta,
  handbookParts as staticParts,
  handbookSections as staticSections,
  documentControl as staticDocumentControl,
} from '../config/handbookContent';

/** Edition primary label + muted formal code (audit / acknowledgement identity). */
const EditionLabel = ({ code, className = '' }) => {
  const label = formatHandbookEditionLabel(code);
  const showCode = label && label !== code;
  return (
    <span className={className} title={showCode ? `Edition code ${code}` : undefined}>
      <span className="font-semibold text-gray-900">{label || code}</span>
      {showCode && (
        <span className="block text-xs font-normal text-gray-400 tabular-nums mt-0.5">{code}</span>
      )}
    </span>
  );
};

// Each Part owns one accent hue so colour becomes a learnable cue in the TOC.
const PART_ACCENT = {
  welcome: { dot: 'bg-blue-500', text: 'text-blue-700', soft: 'bg-blue-50' },
  employment: { dot: 'bg-indigo-500', text: 'text-indigo-700', soft: 'bg-indigo-50' },
  leave: { dot: 'bg-teal-500', text: 'text-teal-700', soft: 'bg-teal-50' },
  benefits: { dot: 'bg-amber-500', text: 'text-amber-700', soft: 'bg-amber-50' },
  workplace: { dot: 'bg-violet-500', text: 'text-violet-700', soft: 'bg-violet-50' },
};

const APPLIES_TO = {
  all: { label: 'All employees', cls: 'bg-gray-100 text-gray-600' },
  staff: { label: 'Office staff', cls: 'bg-blue-100 text-blue-700' },
  worker: { label: 'Workers, Foremen & Supervisors', cls: 'bg-amber-100 text-amber-700' },
};

const AppliesToBadge = ({ value }) => {
  const a = APPLIES_TO[value] || APPLIES_TO.all;
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${a.cls}`}>
      {a.label}
    </span>
  );
};

// Grouped table of contents (shared by the desktop sidebar and mobile drawer).
const TocList = ({ sections, parts, activeId, onNavigate }) => {
  const grouped = useMemo(() => (
    parts
      .map((part) => ({ ...part, items: sections.filter((s) => s.part === part.id) }))
      .filter((part) => part.items.length > 0)
  ), [sections, parts]);

  if (grouped.length === 0) {
    return <p className="text-small px-2 py-3">No sections match your search.</p>;
  }

  return (
    <nav className="space-y-5" aria-label="Handbook contents">
      {grouped.map((part) => {
        const accent = PART_ACCENT[part.id] || PART_ACCENT.welcome;
        return (
          <div key={part.id} className="space-y-1.5">
            <div className="flex items-center gap-2 px-2">
              <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${accent.text}`}>{part.label}</span>
            </div>
            <ul className="space-y-0.5">
              {part.items.map((s) => {
                const active = activeId === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => onNavigate(s.id)}
                      className={`group w-full flex items-start gap-2 text-left px-2 py-1.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                        active ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className={`text-xs tabular-nums mt-0.5 w-6 flex-shrink-0 ${active ? 'text-blue-700' : 'text-gray-400'}`}>
                        {s.number ? s.number : ''}
                      </span>
                      <span className="text-sm leading-snug">{s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
};

const WHATS_NEW_KIND = {
  new: 'bg-blue-100 text-blue-700',
  updated: 'bg-amber-100 text-amber-700',
};

// Acknowledgement gate: prompts the employee to sign off the live edition and
// surfaces "what's new" since the edition they last acknowledged.
const AckBanner = () => {
  const { data: ack, isLoading } = useHandbookAcknowledgement();
  const acknowledge = useAcknowledgeHandbook();

  if (isLoading || !ack) return null;

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  if (ack.acknowledged) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 bg-emerald-50/70">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-900">
            You acknowledged the{' '}
            <strong>{formatHandbookEditionLabel(ack.edition.code)} edition</strong>
            {ack.acknowledgedAt ? ` on ${fmt(ack.acknowledgedAt)}` : ''}.
          </p>
        </div>
      </div>
    );
  }

  const whatsNew = ack.whatsNew || [];
  const showList = !ack.firstTime && whatsNew.length > 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-amber-200 overflow-hidden">
      <div className="px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <History className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-card-header">
              {ack.firstTime ? 'Please read and acknowledge the handbook' : "What's new since you last read"}
            </h2>
            <p className="text-body mt-0.5">
              {ack.firstTime
                ? `${formatHandbookEditionLabel(ack.edition.code)} edition, effective ${ack.edition.effectiveDate}.`
                : `The ${formatHandbookEditionLabel(ack.edition.code)} edition is now in effect${
                    ack.lastAcknowledged
                      ? `; you last acknowledged the ${formatHandbookEditionLabel(ack.lastAcknowledged.code)} edition on ${fmt(ack.lastAcknowledged.acknowledgedAt)}.`
                      : '.'
                  }`}
            </p>

            {showList && (
              <ul className="mt-3 space-y-1.5">
                {whatsNew.slice(0, 8).map((c) => (
                  <li key={c.sectionKey} className="flex items-start gap-2 text-sm">
                    <span className={`inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded mt-0.5 ${WHATS_NEW_KIND[c.kind] || WHATS_NEW_KIND.updated}`}>
                      {c.kind}
                    </span>
                    <span className="text-gray-700 leading-relaxed">
                      {c.title}
                      {c.changeSummary ? <span className="text-gray-500"> &mdash; {c.changeSummary}</span> : null}
                    </span>
                  </li>
                ))}
                {whatsNew.length > 8 && (
                  <li className="text-xs text-gray-400 pl-1">+{whatsNew.length - 8} more updated section(s)</li>
                )}
              </ul>
            )}

            <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => acknowledge.mutate()}
                disabled={acknowledge.isPending}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {acknowledge.isPending ? 'Recording...' : 'I have read and acknowledge'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SelfServiceHandbook = () => {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('');
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const sectionRefs = useRef({});

  useBodyScrollLock(mobileTocOpen);

  // Escape dismisses the mobile TOC drawer (DS: floating UI must support Escape).
  useEffect(() => {
    if (!mobileTocOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileTocOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileTocOpen]);

  // Live content from the API, with the bundled static module as an instant,
  // always-available fallback (covers first paint, offline and API errors).
  const { data: handbook } = useHandbook();
  const handbookMeta = handbook?.meta || staticMeta;
  const handbookParts = handbook?.parts || staticParts;
  const allSections = handbook?.sections || staticSections;
  const documentControl = handbook?.documentControl || staticDocumentControl;

  const q = query.trim().toLowerCase();
  const filteredSections = useMemo(
    () => filterDocumentSections(allSections, query),
    [allSections, query],
  );

  const scrollToSection = useCallback((id) => {
    setMobileTocOpen(false);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
      if (window.history?.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
  }, []);

  // Scroll-spy: highlight the section currently nearest the top of the viewport.
  useEffect(() => {
    const ids = filteredSections.map((s) => s.id);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [filteredSections]);

  // Reading progress + back-to-top visibility.
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const height = doc.scrollHeight - doc.clientHeight;
      setProgress(height > 0 ? Math.min(100, (scrollTop / height) * 100) : 0);
      setShowTop(scrollTop > 480);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Deep-link support: jump to #section on first load.
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sectionRefs.current[hash]) {
      setTimeout(() => scrollToSection(hash), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resultCount = filteredSections.length;

  return (
    <DashboardLayout>
      {/* Reading progress */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-transparent z-40 pointer-events-none">
        <div className="h-full bg-blue-600 transition-[width] duration-150 ease-out" style={{ width: `${progress}%` }} />
      </div>

      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-20">
        {/* Cover page */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="relative bg-gradient-to-b from-blue-50/60 via-white to-white px-6 py-8 sm:px-10 sm:py-12">
            <div className="flex items-center justify-between gap-4">
              <img src={weplLogo} alt="Winner Engineering" className="h-9 sm:h-10 w-auto" />
              <InfoBadge
                color="slate"
                label={handbookMeta.classification}
                title={`${handbookMeta.classification} — for company use only`}
              />
            </div>

            <div className="mt-6 flex justify-center">
              <img
                src={handbookCover}
                alt="A tree being nurtured by people, symbolising growth and care"
                className="w-full max-w-md h-auto select-none pointer-events-none"
                draggable="false"
              />
            </div>

            <div className="mt-6 text-center max-w-2xl mx-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">{handbookMeta.company}</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">{handbookMeta.title}</h1>
              <p className="text-body mt-1">{handbookMeta.jurisdiction}</p>
              <p className="mt-5 text-sm sm:text-base italic text-gray-600 leading-relaxed">
                &ldquo;Nurturing Talent, Empowering Success: People at the Heart of Our Business.&rdquo;
              </p>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-500">
              <span title={`Edition code ${handbookMeta.version}`}>
                {formatHandbookEditionLabel(handbookMeta.version)} edition
              </span>
              <span className="hidden sm:inline text-gray-300">&bull;</span>
              <span>Effective {handbookMeta.effectiveDate}</span>
              <span className="hidden sm:inline text-gray-300">&bull;</span>
              <span>{handbookMeta.documentRef}</span>
            </div>
          </div>
        </div>

        {/* Acknowledgement gate / what's new */}
        <AckBanner />

        {/* Document header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start gap-4">
              <Link
                to="/self-service"
                aria-label="Back to Self-Service"
                title="Back to Self-Service"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <h1 className="text-section-header truncate min-w-0">{handbookMeta.title}</h1>
                  <InfoBadge
                    color="slate"
                    label={handbookMeta.classification}
                    title={`${handbookMeta.classification} — for company use only`}
                    className="flex-shrink-0"
                  />
                </div>
                <p className="text-body mt-0.5">{handbookMeta.company} &middot; {handbookMeta.jurisdiction}</p>
              </div>
            </div>

            {/* Meta grid — standalone block, spans the full card width. */}
            <dl className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                {
                  icon: FileText,
                  label: 'Edition',
                  value: formatHandbookEditionLabel(handbookMeta.version),
                  title: `Edition code ${handbookMeta.version}`,
                },
                { icon: CalendarDays, label: 'Effective', value: handbookMeta.effectiveDate },
                { icon: UserCog, label: 'Custodian', value: handbookMeta.custodian },
                { icon: History, label: 'Last updated', value: handbookMeta.lastUpdated },
              ].map((m) => (
                <div key={m.label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                  <dt className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <m.icon className="w-3.5 h-3.5" />
                    {m.label}
                  </dt>
                  <dd className="text-sm font-semibold text-gray-900 mt-0.5 truncate" title={m.title || m.value}>
                    {m.value}
                  </dd>
                </div>
              ))}
            </dl>

            {/* Search + actions — DS Type 8: DebouncedSearchInput capped at max-w-md;
                Print sits right (toolbar pattern). Document search ≠ full-bleed table filter. */}
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <DebouncedSearchInput
                value={query}
                onSearch={setQuery}
                placeholder="Search the handbook..."
                theme="employee"
                className="w-full max-w-md"
              />
              <button
                type="button"
                onClick={() => window.print()}
                className="btn btn-secondary flex-shrink-0 self-start sm:self-auto"
              >
                <Printer className="w-4 h-4" />
                Print / Save PDF
              </button>
            </div>
            {query && (
              <p className="text-small mt-2">{resultCount} section{resultCount === 1 ? '' : 's'} found</p>
            )}
          </div>
        </div>

        {/* Two-column reader */}
        <div className="lg:grid lg:grid-cols-[280px,1fr] lg:gap-8 lg:items-start">
          {/* Desktop sidebar — viewport-capped so TOC scrolls independently of the main column */}
          <aside className="hidden lg:block lg:sticky lg:top-6 lg:self-start">
            <div className="card flex flex-col max-h-[calc(100vh-3rem)]">
              <div className="card-content flex flex-col min-h-0 flex-1">
                <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                  <ListTree className="w-4 h-4 text-blue-800" />
                  <h2 className="text-card-header">Contents</h2>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mr-2 pr-2">
                  <TocList sections={filteredSections} parts={handbookParts} activeId={activeId} onNavigate={scrollToSection} />
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="space-y-6 mt-6 lg:mt-0">
            {resultCount === 0 ? (
              <div className="card">
                <div className="card-content text-center py-12">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-subsection-header">No matching sections</h3>
                  <p className="text-body mt-1">Try a different keyword, or clear your search to browse the full handbook.</p>
                  <button type="button" onClick={() => setQuery('')} className="btn btn-primary mt-4 mx-auto">
                    Clear search
                  </button>
                </div>
              </div>
            ) : (
              filteredSections.map((s) => {
                const accent = PART_ACCENT[s.part] || PART_ACCENT.welcome;
                return (
                  <article
                    key={s.id}
                    id={s.id}
                    ref={(el) => { sectionRefs.current[s.id] = el; }}
                    className="card scroll-mt-24"
                  >
                    <div className="card-content">
                      <header className="flex items-start gap-3 pb-4 mb-4 border-b border-gray-100">
                        {s.number ? (
                          <span className={`flex-shrink-0 w-9 h-9 rounded-full ${accent.soft} ${accent.text} font-bold text-sm flex items-center justify-center tabular-nums`}>
                            {s.number}
                          </span>
                        ) : (
                          <span className={`flex-shrink-0 w-9 h-9 rounded-full ${accent.soft} flex items-center justify-center`}>
                            <span className={`w-2 h-2 rounded-full ${accent.dot}`} />
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-subsection-header">{s.title}</h2>
                            <AppliesToBadge value={s.appliesTo} />
                          </div>
                          {s.summary && <p className="text-small mt-1">{s.summary}</p>}
                          {s.lastUpdated && (
                            <p className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
                              <CalendarDays className="w-3.5 h-3.5" />
                              Last updated {s.lastUpdated}
                            </p>
                          )}
                        </div>
                      </header>
                      <div className="space-y-4">
                        {s.blocks.map((b, i) => <Block key={i} block={b} />)}
                      </div>
                    </div>
                  </article>
                );
              })
            )}

            {/* Document Control - version history, mirroring the parent handbook. */}
            {!q && (
              <article className="card">
                <div className="card-content">
                  <header className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-100">
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                      <History className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="text-subsection-header">Document Control</h2>
                      <p className="text-small mt-1">Change history for this handbook. Maintained by {handbookMeta.custodian}.</p>
                    </div>
                  </header>

                  {/* Changes in this edition — the latest release's section-level change log. */}
                  {documentControl[0]?.changes?.length > 0 && (
                    <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-blue-900 mb-2.5">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        Changes in this edition ({formatHandbookEditionLabel(documentControl[0].version)})
                      </p>
                      <ul className="space-y-1.5">
                        {documentControl[0].changes.map((c) => (
                          <li key={c.sectionKey} className="flex items-start gap-2 text-sm">
                            <span className={`inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded mt-0.5 ${c.kind === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                              {c.kind}
                            </span>
                            <span className="text-gray-700 leading-relaxed">
                              {c.clauseNumber ? `${c.clauseNumber}. ` : ''}{c.title}
                              {c.summary ? <span className="text-gray-500"> &mdash; {c.summary}</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Mobile: one card per version — a 5-column history is unreadable
                      squeezed onto a phone, especially the change summary. */}
                  <div className="md:hidden space-y-3">
                    {documentControl.map((row) => (
                      <div key={row.version} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <EditionLabel code={row.version} />
                          <span className="text-xs text-gray-500 whitespace-nowrap pt-0.5">
                            Effective {row.date}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          By {row.author}{row.approver ? ` \u00b7 Approved by ${row.approver}` : ''}
                        </div>
                        <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">{row.summary}</p>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: full table */}
                  <div className="hidden md:block overflow-x-auto -mx-2 px-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">
                          <th className="py-2 pr-4 font-semibold">Edition</th>
                          <th className="py-2 pr-4 font-semibold">Effective</th>
                          <th className="py-2 pr-4 font-semibold">Author</th>
                          <th className="py-2 pr-4 font-semibold">Approved by</th>
                          <th className="py-2 font-semibold">Summary of changes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {documentControl.map((row) => (
                          <tr key={row.version} className="align-top">
                            <td className="py-3 pr-4 whitespace-nowrap">
                              <EditionLabel code={row.version} />
                            </td>
                            <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{row.date}</td>
                            <td className="py-3 pr-4 text-gray-600">{row.author}</td>
                            <td className="py-3 pr-4 text-gray-600">{row.approver}</td>
                            <td className="py-3 text-gray-700">{row.summary}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-small mt-4">
                    Questions about any policy? Contact HR at{' '}
                    <a href="mailto:hrm@winner-e.sg" className="text-blue-600 hover:underline font-medium">hrm@winner-e.sg</a>.
                  </p>
                </div>
              </article>
            )}
          </main>
        </div>
      </div>

      {/* Mobile contents FAB + TOC drawer — portaled to document.body.
          Must NOT stay inside DashboardLayout's scrolling <main>: fixed
          descendants of overflow scrollports get clipped/offset after scroll,
          which left users staring at a blank white panel. */}
      <Portal>
        {!mobileTocOpen && (
          <button
            type="button"
            onClick={() => setMobileTocOpen(true)}
            className="lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-[9998] inline-flex items-center gap-2 px-5 py-3 rounded-full bg-blue-800 text-white shadow-lg font-medium text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
          >
            <Menu className="w-4 h-4" />
            Contents
          </button>
        )}

        {mobileTocOpen && (
          <div
            className="lg:hidden fixed inset-0 z-[9999] flex"
            role="dialog"
            aria-modal="true"
            aria-labelledby="handbook-toc-title"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setMobileTocOpen(false)}
              aria-hidden="true"
            />
            <div
              className="relative ml-auto w-[85%] max-w-sm bg-white h-full shadow-xl flex flex-col"
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <ListTree className="w-4 h-4 text-blue-800 flex-shrink-0" />
                  <h2 id="handbook-toc-title" className="text-card-header">Contents</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileTocOpen(false)}
                  aria-label="Close contents"
                  className="icon-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div
                className="flex-1 overflow-y-auto overscroll-y-contain p-4 pb-safe"
                style={{ touchAction: 'pan-y' }}
              >
                <TocList
                  sections={filteredSections}
                  parts={handbookParts}
                  activeId={activeId}
                  onNavigate={scrollToSection}
                />
              </div>
            </div>
          </div>
        )}
      </Portal>

      {/* Back to top */}
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
          title="Back to top"
          className="hidden lg:flex fixed bottom-6 right-6 z-30 w-11 h-11 items-center justify-center rounded-full bg-white text-gray-600 shadow-lg border border-gray-100 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </DashboardLayout>
  );
};

export default SelfServiceHandbook;

